import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Icon from '../Utils/Icon';
import classes from './styles.css';

/**
 * Секретный чат — окно переписки и список разговоров.
 *
 * Компонент один на обе стороны: клиент и сотрудник видят одну переписку и
 * ходят в разные ручки, поэтому загрузка и отправка приходят пропсами, а про
 * сторону компонент знает ровно одно — какие сообщения считать своими.
 *
 * Сообщения живут сутки и исчезают у обеих сторон, поэтому лента ничего не
 * копит: она перечитывается по таймеру, иначе на экране оставалось бы то,
 * чего на сервере уже нет.
 */
const REFRESH_MS = 7000;

/** Палитра аватаров: цвет закреплён за собеседником, а не пляшет от сортировки. */
const AVATAR_COLORS = ['#4a7dd6', '#2e9e83', '#c2683a', '#7b5fc4', '#3d8bb5', '#b4557f'];

export function avatarColor(seed) {
    const key = String(seed || '');
    let sum = 0;

    for (let i = 0; i < key.length; i += 1) {
        sum += key.charCodeAt(i);
    }

    return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

export function initials(name) {
    const parts = String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (parts.length === 0) return '#';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

    return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** Размер файла человеческими словами. */
export function formatFileSize(bytes) {
    const size = Number(bytes || 0);

    if (size < 1024) return size + ' Б';
    if (size < 1024 * 1024) return Math.round(size / 1024) + ' КБ';

    return (size / 1024 / 1024).toFixed(size < 10 * 1024 * 1024 ? 1 : 0) + ' МБ';
}

/** Картинку показываем прямо в ленте, остальное — карточкой с именем. */
export function isImage(mime) {
    return String(mime || '').indexOf('image/') === 0;
}

function timeOf(value) {
    const date = new Date(value);

    return isNaN(date.getTime())
        ? ''
        : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function dayOf(value) {
    const date = new Date(value);

    return isNaN(date.getTime()) ? '' : date.toDateString();
}

/**
 * Аватар собеседника: инициалы в круге. Картинок у аккаунтов нет, а список
 * из одинаковых строк читается плохо.
 */
export function Avatar({ name, seed, size }) {
    const style = {
        background: avatarColor(seed),
        width: size || 40,
        height: size || 40,
        fontSize: (size || 40) / 2.6,
    };

    return (
        <span className={classes.Avatar} style={style}>
            {initials(name)}
        </span>
    );
}

/** Левая колонка: разговоры, свежие сверху. */
export function ChatList({ chats, activeId, onPick, onNew, isLoading, titleOf, subtitleOf }) {
    const intl = useIntl();

    return (
        <div className={classes.List}>
            <div className={classes.ListHead}>
                <span className={classes.ListTitle}>
                    <FormattedMessage id="SecretChat.list.title" defaultMessage="Разговоры" />
                </span>
                <button
                    type="button"
                    className={classes.NewButton}
                    onClick={onNew}
                    title={intl.formatMessage({
                        id: 'SecretChat.button.new',
                        defaultMessage: 'Новый чат',
                    })}
                >
                    <Icon icon="plus" size="14px" />
                    <FormattedMessage id="SecretChat.button.new" defaultMessage="Новый чат" />
                </button>
            </div>

            <div className={classes.ListBody}>
                {isLoading ? (
                    <div className={classes.Muted}>
                        <FormattedMessage id="SecretChat.loading" defaultMessage="Загрузка…" />
                    </div>
                ) : null}

                {!isLoading && chats.length === 0 ? (
                    <div className={classes.Muted}>
                        <FormattedMessage id="SecretChats.empty" defaultMessage="Чатов пока нет" />
                    </div>
                ) : null}

                {chats.map(function (chat) {
                    const title = titleOf ? titleOf(chat) : chat.subject;
                    const subtitle = subtitleOf ? subtitleOf(chat) : chat.subject;

                    return (
                        <button
                            type="button"
                            key={chat.id}
                            className={chat.id === activeId ? `${classes.Row} ${classes.RowActive}` : classes.Row}
                            onClick={() => onPick(chat.id)}
                        >
                            <Avatar name={title} seed={chat.id} />
                            <span className={classes.RowText}>
                                <span className={classes.RowTop}>
                                    <span className={classes.RowTitle}>{title}</span>
                                    <span className={classes.RowTime}>{timeOf(chat.lastMessageAt)}</span>
                                </span>
                                <span className={classes.RowBottom}>
                                    <span className={classes.RowSubtitle}>{subtitle}</span>
                                    {chat.unread > 0 ? <span className={classes.Badge}>{chat.unread}</span> : null}
                                </span>
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/** Правая колонка: сама переписка. */
export default function SecretChat({
    chat,
    side,
    loadMessages,
    sendMessage,
    onRead,
    ttlHours,
    titleOf,
    downloadFile,
}) {
    const intl = useIntl();
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    // Выбранные, но ещё не отправленные файлы. Держим здесь, а не в форме:
    // человек может передумать и убрать файл до отправки.
    const [files, setFiles] = useState([]);
    const fileInputRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState(null);

    const bottomRef = useRef(null);
    const mountedRef = useRef(true);
    const chatId = chat && chat.id;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const refresh = useCallback(
        async function (silent) {
            if (!chatId) return;
            if (!silent) setIsLoading(true);

            try {
                const data = await loadMessages(chatId);
                if (!mountedRef.current) return;
                setMessages((data && data.items) || []);
                setError(null);
            } catch (e) {
                if (!mountedRef.current) return;
                setError(
                    intl.formatMessage({
                        id: 'SecretChat.error.load',
                        defaultMessage: 'Не удалось загрузить переписку',
                    })
                );
            } finally {
                if (mountedRef.current && !silent) setIsLoading(false);
            }
        },
        [chatId, loadMessages, intl]
    );

    useEffect(
        function () {
            if (!chatId) return undefined;

            refresh(false);
            if (onRead) onRead(chatId);

            const timer = setInterval(() => refresh(true), REFRESH_MS);
            return () => clearInterval(timer);
        },
        [chatId, refresh, onRead]
    );

    useEffect(
        function () {
            if (bottomRef.current && bottomRef.current.scrollIntoView) {
                bottomRef.current.scrollIntoView({ block: 'end' });
            }
        },
        [messages]
    );

    /** Лента с разделителями дат — как в мессенджере. */
    const feed = useMemo(
        function () {
            const rows = [];
            let lastDay = null;

            messages.forEach(function (message) {
                const day = dayOf(message.createdAt);

                if (day && day !== lastDay) {
                    rows.push({ kind: 'day', id: 'day-' + day, value: message.createdAt });
                    lastDay = day;
                }

                rows.push({ kind: 'message', id: message.id, value: message });
            });

            return rows;
        },
        [messages]
    );

    async function onSubmit(e) {
        if (e && e.preventDefault) e.preventDefault();

        const body = text.trim();
        // С файлом текст необязателен: «вот выписка» без подписи — нормально.
        if ((!body && files.length === 0) || isSending || !chatId) return;

        setIsSending(true);
        try {
            const data = await sendMessage(chatId, body, files);
            if (!mountedRef.current) return;
            setMessages((data && data.items) || []);
            setText('');
            setFiles([]);
            if (fileInputRef.current) fileInputRef.current.value = '';
            setError(null);
        } catch (err) {
            if (!mountedRef.current) return;
            // Сервер объясняет отказ по файлу словами («больше 50 МБ»,
            // «исполняемые файлы через чат не передаются») — показываем их,
            // иначе человек не поймёт, почему сообщение не ушло.
            const serverMessage =
                err && err.response && err.response.data && err.response.data.message;
            setError(
                serverMessage ||
                    intl.formatMessage({ id: 'SecretChat.error.send', defaultMessage: 'Сообщение не отправлено' })
            );
        } finally {
            if (mountedRef.current) setIsSending(false);
        }
    }

    /**
     * Скачивание идёт запросом с токеном, а не обычной ссылкой: у ссылки нет
     * заголовка авторизации, и сервер ответил бы отказом.
     */
    async function onDownload(file) {
        if (!chatId || !downloadFile) return;

        try {
            const blob = await downloadFile(chatId, file.id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = file.name || 'file';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            if (!mountedRef.current) return;
            setError(
                intl.formatMessage({
                    id: 'SecretChat.error.download',
                    defaultMessage: 'Файл не скачался — возможно, он уже удалён',
                })
            );
        }
    }

    /** Сколько сообщению осталось жить — подсказкой, чтобы не мусорить в ленте. */
    function leftHint(expiresAt) {
        const left = new Date(expiresAt).getTime() - Date.now();
        if (isNaN(left) || left <= 0) return '';

        const hours = Math.floor(left / 3600000);
        const minutes = Math.floor((left % 3600000) / 60000);

        return hours > 0
            ? intl.formatMessage(
                  { id: 'SecretChat.left.hours', defaultMessage: 'исчезнет через {hours} ч {minutes} мин' },
                  { hours, minutes }
              )
            : intl.formatMessage(
                  { id: 'SecretChat.left.minutes', defaultMessage: 'исчезнет через {minutes} мин' },
                  { minutes }
              );
    }

    function formatDay(value) {
        const date = new Date(value);
        if (isNaN(date.getTime())) return '';

        const today = new Date().toDateString();
        if (date.toDateString() === today) {
            return intl.formatMessage({ id: 'SecretChat.day.today', defaultMessage: 'Сегодня' });
        }

        return date.toLocaleDateString();
    }

    if (!chat) {
        return (
            <div className={classes.Empty}>
                <span className={classes.EmptyIcon}>
                    <Icon icon="lock" size="26px" />
                </span>
                <span className={classes.EmptyTitle}>
                    <FormattedMessage id="SecretChat.empty.pick" defaultMessage="Выберите чат или начните новый" />
                </span>
                <span className={classes.EmptyNote}>
                    <FormattedMessage
                        id="SecretChat.note.ttl"
                        defaultMessage="Переписка зашифрована и удаляется через {hours} ч"
                        values={{ hours: ttlHours || 24 }}
                    />
                </span>
            </div>
        );
    }

    const title = titleOf ? titleOf(chat) : chat.subject;

    return (
        <div className={classes.Chat}>
            <div className={classes.Head}>
                <Avatar name={title} seed={chat.id} size={36} />
                <span className={classes.HeadText}>
                    <span className={classes.Subject}>{title}</span>
                    <span className={classes.Note}>
                        <Icon icon="lock" size="11px" />
                        <FormattedMessage
                            id="SecretChat.note.ttl"
                            defaultMessage="Переписка зашифрована и удаляется через {hours} ч"
                            values={{ hours: ttlHours || 24 }}
                        />
                    </span>
                </span>
            </div>

            <div className={classes.Messages}>
                {isLoading ? (
                    <div className={classes.Muted}>
                        <FormattedMessage id="SecretChat.loading" defaultMessage="Загрузка…" />
                    </div>
                ) : null}

                {!isLoading && messages.length === 0 ? (
                    <div className={classes.Muted}>
                        <FormattedMessage id="SecretChat.empty.messages" defaultMessage="Пока пусто — напишите первым" />
                    </div>
                ) : null}

                {feed.map(function (row) {
                    if (row.kind === 'day') {
                        return (
                            <div key={row.id} className={classes.Day}>
                                <span>{formatDay(row.value)}</span>
                            </div>
                        );
                    }

                    const message = row.value;
                    const mine = message.author === side;

                    return (
                        <div key={row.id} className={mine ? `${classes.Line} ${classes.LineMine}` : classes.Line}>
                            {mine ? null : <Avatar name={message.authorName} seed={message.authorName} size={28} />}
                            <div
                                className={mine ? `${classes.Bubble} ${classes.BubbleMine}` : classes.Bubble}
                                title={leftHint(message.expiresAt)}
                            >
                                {mine ? null : <div className={classes.Author}>{message.authorName}</div>}
                                {message.body ? <div className={classes.Body}>{message.body}</div> : null}
                                {(message.files || []).map(function (file) {
                                    return (
                                        <button
                                            type="button"
                                            key={file.id}
                                            className={classes.File}
                                            onClick={() => onDownload(file)}
                                            title={intl.formatMessage({
                                                id: 'SecretChat.file.download',
                                                defaultMessage: 'Скачать файл',
                                            })}
                                        >
                                            <Icon icon="document-download" size="14px" />
                                            <span className={classes.FileName}>{file.name}</span>
                                            <span className={classes.FileSize}>{formatFileSize(file.size)}</span>
                                        </button>
                                    );
                                })}
                                <div className={classes.Time}>{timeOf(message.createdAt)}</div>
                            </div>
                        </div>
                    );
                })}

                <div ref={bottomRef} />
            </div>

            {error ? <div className={classes.Error}>{error}</div> : null}

            {files.length > 0 ? (
                <div className={classes.Picked}>
                    {files.map(function (file, index) {
                        return (
                            <span className={classes.PickedItem} key={file.name + index}>
                                <span className={classes.FileName}>{file.name}</span>
                                <span className={classes.FileSize}>{formatFileSize(file.size)}</span>
                                <button
                                    type="button"
                                    className={classes.PickedRemove}
                                    onClick={() => setFiles(files.filter((_, i) => i !== index))}
                                >
                                    ×
                                </button>
                            </span>
                        );
                    })}
                </div>
            ) : null}

            <form className={classes.Form} onSubmit={onSubmit}>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className={classes.HiddenInput}
                    onChange={(e) => {
                        setFiles(files.concat(Array.prototype.slice.call(e.target.files || [])));
                    }}
                />
                <button
                    type="button"
                    className={classes.Attach}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    title={intl.formatMessage({
                        id: 'SecretChat.file.attach',
                        defaultMessage: 'Приложить файл',
                    })}
                >
                    <Icon icon="cloud-upload" size="16px" />
                </button>
                <textarea
                    className={classes.Input}
                    value={text}
                    rows={1}
                    maxLength={20000}
                    placeholder={intl.formatMessage({
                        id: 'SecretChat.placeholder',
                        defaultMessage: 'Сообщение…',
                    })}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        // Enter отправляет, Shift+Enter — перенос строки.
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            onSubmit(e);
                        }
                    }}
                />
                <button
                    className={classes.Send}
                    type="submit"
                    disabled={isSending || (!text.trim() && files.length === 0)}
                    title={intl.formatMessage({ id: 'SecretChat.button.send', defaultMessage: 'Отправить' })}
                >
                    <Icon icon="share" size="14px" />
                </button>
            </form>
        </div>
    );
}



// WEBPACK FOOTER //
// ./src/Components/SecretChat/index.js